import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Account, DebtAccount, DebtPayment } from '../types';

interface ConfirmPaymentModal {
  show: boolean;
  payment: DebtPayment | null;
  subscription: any | null;
  type: 'debt' | 'subscription' | null;
}

const Dashboard: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [debts, setDebts] = useState<DebtAccount[]>([]);
  const [upcomingPayments, setUpcomingPayments] = useState<DebtPayment[]>([]);
  const [dueSubscriptions, setDueSubscriptions] = useState<any[]>([]);
  const [monthlyExpenses, setMonthlyExpenses] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [dailyBudget, setDailyBudget] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState<ConfirmPaymentModal>({ show: false, payment: null, subscription: null, type: null });
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [showFixkostenDetails, setShowFixkostenDetails] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({ show: false, message: '', type: 'success' });
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [accountsRes, debtsRes, paymentsRes, expensesRes, subsRes, transRes, budgetRes] = await Promise.all([
        api.get('/accounts'),
        api.get('/debts'),
        api.get('/debts/upcoming-payments'),
        api.get('/dashboard/monthly-expenses'),
        api.get('/subscriptions/due'),
        api.get('/transactions'),
        api.get('/dashboard/daily-budget')
      ]);

      setAccounts(accountsRes.data.data.accounts);
      setDebts(debtsRes.data.data.debtAccounts);
      setUpcomingPayments(paymentsRes.data.data.upcomingPayments);
      setMonthlyExpenses(expensesRes.data.data);
      setTransactions(transRes.data.data.transactions);
      setDailyBudget(budgetRes.data.data);
      
      // Abos nach Buchungstag sortieren
      const subs = subsRes.data.data.subscriptions;
      subs.sort((a: any, b: any) => {
        const dayA = a.bookingDay || new Date(a.date).getDate();
        const dayB = b.bookingDay || new Date(b.date).getDate();
        return dayA - dayB;
      });
      setDueSubscriptions(subs);
    } catch (error) {
      console.error('Fehler beim Laden der Dashboard-Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedAccountId) {
      setToast({ show: true, message: 'Bitte wählen Sie ein Konto aus', type: 'error' });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
      return;
    }

    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      setToast({ show: true, message: 'Bitte geben Sie einen gültigen Betrag ein', type: 'error' });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
      return;
    }

    try {
      if (confirmModal.type === 'debt' && confirmModal.payment) {
        await api.post(`/debts/${confirmModal.payment.debtAccountId}/confirm-payment`, {
          accountId: selectedAccountId,
          amount: parseFloat(paymentAmount),
          paymentDate: new Date().toISOString()
        });
      } else if (confirmModal.type === 'subscription' && confirmModal.subscription) {
        // Buche Abo mit gewähltem Konto und Betrag
        await api.post(`/subscriptions/${confirmModal.subscription.id}/book`, {
          accountId: selectedAccountId,
          amount: -Math.abs(parseFloat(paymentAmount))
        });
      }

      setConfirmModal({ show: false, payment: null, subscription: null, type: null });
      setSelectedAccountId('');
      setPaymentAmount('');
      
      // Daten neu laden
      await fetchDashboardData();
      
      setToast({ show: true, message: '✓ Zahlung erfolgreich bestätigt!', type: 'success' });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } catch (error: any) {
      console.error('Fehler beim Bestätigen der Zahlung:', error);
      setToast({ show: true, message: error.response?.data?.message || 'Fehler beim Bestätigen der Zahlung', type: 'error' });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    }
  };


  const isMobile = window.innerWidth <= 768;
  const totalBalance = accounts.reduce((sum, acc) => sum + parseFloat(acc.balance.toString()), 0);
  // Nur aktive Schulden für Gesamtschulden zählen
  const totalDebt = debts
    .filter(debt => debt.status === 'ACTIVE')
    .reduce((sum, debt) => sum + parseFloat(debt.currentBalance.toString()), 0);
  const netWorth = totalBalance - totalDebt;

  // Summe aller tatsächlich geleisteten Zahlungen aus DEBT_PAYMENT Transaktionen
  const totalPaidAmount = transactions
    .filter((tx: any) => tx.category === 'DEBT_PAYMENT')
    .reduce((sum: number, tx: any) => sum + Math.abs(parseFloat(tx.amount || '0')), 0);

  // Bezahlte Schulden zählen (nur Inkasso und Kredite, keine Kreditkarten)
  const paidOffDebts = debts.filter(debt => 
    debt.status === 'PAID_OFF' && 
    ['COLLECTION', 'LOAN', 'MORTGAGE', 'STUDENT_LOAN'].includes(debt.type)
  );
  const activeLoanDebts = debts.filter(debt =>
    debt.status === 'ACTIVE' &&
    ['COLLECTION', 'LOAN', 'MORTGAGE', 'STUDENT_LOAN'].includes(debt.type)
  );

  if (loading) {
    return <div className="loading">Lädt...</div>;
  }

  return (
    <div className="container">
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'flex-start', 
        marginBottom: '20px',
        gap: isMobile ? '15px' : '0'
      }}>
        <h1 style={{ fontSize: isMobile ? '24px' : '32px' }}>Willkommen, {user.firstName}!</h1>
        
        {/* Schuldenzähler - nur anzeigen wenn aktive Schulden vorhanden */}
        {activeLoanDebts.length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
            color: 'white',
            padding: isMobile ? '12px 20px' : '15px 25px',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
            textAlign: 'center',
            minWidth: isMobile ? 'auto' : '200px',
            transform: isMobile ? 'none' : 'rotate(-2deg)',
            transition: 'transform 0.3s ease'
          }}
          onMouseEnter={(e) => !isMobile && (e.currentTarget.style.transform = 'rotate(0deg) scale(1.05)')}
          onMouseLeave={(e) => !isMobile && (e.currentTarget.style.transform = 'rotate(-2deg)')}
          >
            <div style={{ fontSize: '12px', fontWeight: '500', opacity: 0.9, marginBottom: '5px' }}>
              ABGEZAHLTE SCHULDEN
            </div>
            <div style={{ fontSize: '36px', fontWeight: 'bold', lineHeight: '1' }}>
              {totalPaidAmount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
            <div style={{ fontSize: '11px', marginTop: '8px', opacity: 0.85 }}>
              {paidOffDebts.length} vollständig abbezahlt
            </div>
            <div style={{ fontSize: '10px', marginTop: '3px', opacity: 0.7 }}>
              {activeLoanDebts.length} noch aktiv
            </div>
          </div>
        )}
      </div>

      {/* Tagesbudget - Kompakt */}
      {dailyBudget && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '15px',
          background: dailyBudget.status === 'critical' 
            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
            : dailyBudget.status === 'warning'
            ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
            : dailyBudget.status === 'good'
            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
            : 'linear-gradient(135deg, #6f42c1 0%, #5a2d9e 100%)',
          color: 'white',
          padding: isMobile ? '12px 16px' : '15px 20px',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          marginBottom: '20px',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          flexWrap: 'wrap',
          maxWidth: '100%'
        }}
        onClick={() => setShowFixkostenDetails(!showFixkostenDetails)}
        onMouseEnter={(e) => !isMobile && (e.currentTarget.style.transform = 'translateY(-2px)')}
        onMouseLeave={(e) => !isMobile && (e.currentTarget.style.transform = 'translateY(0)')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: isMobile ? '24px' : '28px' }}>💰</span>
            <div>
              <div style={{ fontSize: isMobile ? '11px' : '12px', opacity: 0.9, fontWeight: '500' }}>
                Tagesbudget
              </div>
              <div style={{ fontSize: isMobile ? '28px' : '36px', fontWeight: 'bold', lineHeight: '1.1' }}>
                {dailyBudget.dailyBudget.toFixed(2)} €
              </div>
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: isMobile ? '10px' : '20px',
            fontSize: isMobile ? '12px' : '13px',
            flexWrap: 'wrap'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ opacity: 0.8 }}>Verfügbar</div>
              <div style={{ fontWeight: 'bold', fontSize: isMobile ? '16px' : '18px' }}>
                {dailyBudget.availableForDaily.toFixed(0)} €
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ opacity: 0.8 }}>Tage bis Gehalt</div>
              <div style={{ fontWeight: 'bold', fontSize: isMobile ? '16px' : '18px' }}>
                {dailyBudget.daysUntilNextSalary}
              </div>
            </div>
            {!isMobile && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ opacity: 0.8 }}>Fixkosten</div>
                <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                  {dailyBudget.reservedForFixedCosts.toFixed(0)} €
                </div>
              </div>
            )}
          </div>
          
          <div style={{ fontSize: '10px', opacity: 0.7, marginLeft: 'auto' }}>
            {showFixkostenDetails ? '▼ Details' : '▶ Details'}
          </div>
        </div>
      )}

      {/* Fixkosten Details (ausklappbar) */}
      {dailyBudget && showFixkostenDetails && (
        <div className="card" style={{ 
          marginBottom: '20px',
          background: 'linear-gradient(to right, #f8f9fa 0%, #e9ecef 100%)',
          borderRadius: '12px'
        }}>
          <h3 style={{ marginBottom: '15px', fontSize: isMobile ? '16px' : '18px' }}>📊 Budget-Berechnung</h3>
          <div style={{ fontSize: isMobile ? '13px' : '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span>Kontostand (nur Budgetkonten):</span>
              <strong>{dailyBudget.totalBalance.toFixed(2)} €</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', opacity: 0.7 }}>
              <span>- Anstehende Schulden:</span>
              <span>{dailyBudget.upcomingDebtPayments.toFixed(2)} €</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', opacity: 0.7 }}>
              <span>- Abos & Fixkosten (unbezahlt):</span>
              <span>{dailyBudget.upcomingSubscriptions.toFixed(2)} €</span>
            </div>
            <div style={{ borderTop: '2px solid #6f42c1', paddingTop: '10px', marginTop: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: '5px' }}>
                <span>= Verfügbar für {dailyBudget.daysUntilNextSalary} Tage:</span>
                <span>{dailyBudget.availableForDaily.toFixed(2)} €</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: isMobile ? '15px' : '16px', color: '#6f42c1', fontWeight: 'bold' }}>
                <span>= Pro Tag:</span>
                <span>{dailyBudget.dailyBudget.toFixed(2)} €</span>
              </div>
            </div>
            {dailyBudget.lastSalaryDate && (
              <div style={{ 
                marginTop: '15px', 
                paddingTop: '15px',
                borderTop: '1px solid #ddd',
                fontSize: isMobile ? '12px' : '13px', 
                opacity: 0.7
              }}>
                Letztes Gehalt: {dailyBudget.lastSalaryAmount.toFixed(2)} € (vor {dailyBudget.daysSinceLastSalary} Tagen)
                <br />
                Ø Gehaltszyklus: {dailyBudget.avgCycleDays} Tage
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: isMobile ? '15px' : '20px', 
        marginTop: '20px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {/* Gesamtguthaben Card */}
        <div style={{
          background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
          borderRadius: '16px',
          padding: isMobile ? '20px' : '25px',
          color: 'white',
          boxShadow: '0 8px 20px rgba(15, 32, 39, 0.4)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 12px 28px rgba(15, 32, 39, 0.5)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.3)';
          }
        }}>
          <h3 style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '600', opacity: 0.9, marginBottom: '10px' }}>💰 GESAMTGUTHABEN</h3>
          <p style={{ fontSize: isMobile ? '28px' : '32px', fontWeight: 'bold', margin: '10px 0' }}>
            {totalBalance.toFixed(2)} €
          </p>
          <small style={{ opacity: 0.85, fontSize: isMobile ? '12px' : '14px' }}>{accounts.length} Konten</small>
        </div>

        {/* Gesamtschulden Card */}
        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          borderRadius: '16px',
          padding: isMobile ? '20px' : '25px',
          color: 'white',
          boxShadow: '0 8px 20px rgba(245, 87, 108, 0.3)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 12px 28px rgba(245, 87, 108, 0.4)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(245, 87, 108, 0.3)';
          }
        }}>
          <h3 style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '600', opacity: 0.9, marginBottom: '10px' }}>💳 GESAMTSCHULDEN</h3>
          <p style={{ fontSize: isMobile ? '28px' : '32px', fontWeight: 'bold', margin: '10px 0' }}>
            {totalDebt.toFixed(2)} €
          </p>
          <small style={{ opacity: 0.85, fontSize: isMobile ? '12px' : '14px' }}>{debts.filter(d => d.status === 'ACTIVE').length} aktive Schulden</small>
        </div>

        {/* Nettovermögen Card */}
        <div style={{
          background: netWorth >= 0 
            ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' 
            : 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          borderRadius: '16px',
          padding: isMobile ? '20px' : '25px',
          color: 'white',
          boxShadow: netWorth >= 0 
            ? '0 8px 20px rgba(79, 172, 254, 0.3)' 
            : '0 8px 20px rgba(250, 112, 154, 0.3)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = netWorth >= 0 
              ? '0 12px 28px rgba(79, 172, 254, 0.4)' 
              : '0 12px 28px rgba(250, 112, 154, 0.4)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = netWorth >= 0 
              ? '0 8px 20px rgba(79, 172, 254, 0.3)' 
              : '0 8px 20px rgba(250, 112, 154, 0.3)';
          }
        }}>
          <h3 style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '600', opacity: 0.9, marginBottom: '10px' }}>
            {netWorth >= 0 ? '📈 NETTOVERMÖGEN' : '📉 NETTOVERMÖGEN'}
          </h3>
          <p style={{ fontSize: isMobile ? '28px' : '32px', fontWeight: 'bold', margin: '10px 0' }}>
            {netWorth.toFixed(2)} €
          </p>
          <small style={{ opacity: 0.85, fontSize: isMobile ? '12px' : '14px' }}>{netWorth >= 0 ? 'Im Plus' : 'Im Minus'}</small>
        </div>

        {/* Monatliche Fixkosten Card */}
        <div 
          onClick={() => setShowFixkostenDetails(!showFixkostenDetails)}
          style={{
          background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
          borderRadius: '16px',
          padding: isMobile ? '20px' : '25px',
          color: '#333',
          boxShadow: showFixkostenDetails ? '0 12px 28px rgba(252, 182, 159, 0.5)' : '0 8px 20px rgba(252, 182, 159, 0.3)',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          cursor: 'pointer',
          position: 'relative'
        }}
        onMouseEnter={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 12px 28px rgba(252, 182, 159, 0.4)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = showFixkostenDetails ? '0 12px 28px rgba(252, 182, 159, 0.5)' : '0 8px 20px rgba(252, 182, 159, 0.3)';
          }
        }}>
          <div style={{ position: 'absolute', top: '10px', right: '15px', fontSize: isMobile ? '16px' : '20px', opacity: 0.6 }}>
            {showFixkostenDetails ? '▼' : '▶'}
          </div>
          <h3 style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '600', opacity: 0.9, marginBottom: '10px' }}>🔄 MONATLICHE FIXKOSTEN</h3>
          <p style={{ fontSize: isMobile ? '28px' : '32px', fontWeight: 'bold', margin: '10px 0', color: '#ff6b6b' }}>
            {monthlyExpenses ? (monthlyExpenses.recurringExpenses + monthlyExpenses.details.debts.filter((d: any) => d.paymentType !== 'PERCENTAGE').reduce((sum: number, d: any) => sum + d.monthlyPayment, 0)).toFixed(2) : '0.00'} €
          </p>
          <small style={{ fontSize: '11px', opacity: 0.85 }}>
            {monthlyExpenses && (
              <>
                Abos: {monthlyExpenses.recurringExpenses.toFixed(2)} € | 
                Feste Raten: {monthlyExpenses.details.debts.filter((d: any) => d.paymentType !== 'PERCENTAGE').reduce((sum: number, d: any) => sum + d.monthlyPayment, 0).toFixed(2)} €
                {monthlyExpenses.details.debts.some((d: any) => d.paymentType === 'PERCENTAGE') && (
                  <span style={{ display: 'block', marginTop: '5px', opacity: 0.7 }}>
                    + Variable Raten: ~{monthlyExpenses.details.debts.filter((d: any) => d.paymentType === 'PERCENTAGE').reduce((sum: number, d: any) => sum + d.monthlyPayment, 0).toFixed(2)} €
                  </span>
                )}
              </>
            )}
          </small>
        </div>
      </div>

      {/* Monatliche Fixkosten Details - aufklappbar */}
      {showFixkostenDetails && monthlyExpenses && (monthlyExpenses.details.subscriptions.length > 0 || monthlyExpenses.details.debts.length > 0) && (
        <div className="card" style={{ marginTop: '20px', background: '#fff9f5' }}>
          <h2 style={{ fontSize: isMobile ? '20px' : '24px' }}>Monatliche Fixkosten im Detail</h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: isMobile ? '15px' : '20px', 
            marginTop: '15px' 
          }}>
            {monthlyExpenses.details.subscriptions.length > 0 && (
              <div>
                <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>Abos & wiederkehrende Zahlungen</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Beschreibung</th>
                      <th>Kategorie</th>
                      <th>Betrag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyExpenses.details.subscriptions.map((sub: any, idx: number) => (
                      <tr key={idx}>
                        <td>{sub.description}</td>
                        <td><span className="badge badge-info">{sub.category}</span></td>
                        <td style={{ color: '#dc3545' }}>{sub.amount.toFixed(2)} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {monthlyExpenses.details.debts.length > 0 && (
              <div>
                <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>Schulden-Raten</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Schuldenkonto</th>
                      <th>Art</th>
                      <th>Monatliche Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyExpenses.details.debts.map((debt: any, idx: number) => (
                      <tr key={idx}>
                        <td>{debt.name}</td>
                        <td>
                          {debt.paymentType === 'PERCENTAGE' ? (
                            <span className="badge badge-info">{debt.paymentPercent}% vom Saldo</span>
                          ) : (
                            <span className="badge badge-warning">Fest</span>
                          )}
                        </td>
                        <td style={{ color: '#dc3545' }}>{debt.monthlyPayment.toFixed(2)} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card" style={{ 
        marginTop: '20px',
        borderRadius: '16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        border: '1px solid #e9ecef'
      }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
          <span style={{ fontSize: '24px' }}>📅</span> Anstehende Zahlungen (nächste 30 Tage)
        </h2>
        {upcomingPayments.length === 0 ? (
          <p style={{ color: '#666', marginTop: '10px' }}>Keine anstehenden Zahlungen</p>
        ) : (
          <table style={{ marginTop: '10px' }}>
            <thead>
              <tr>
                <th>Schuldenkonto</th>
                <th>Betrag</th>
                <th>Fälligkeitsdatum</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {upcomingPayments.map((payment) => {
                const paymentDate = new Date(payment.paymentDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                paymentDate.setHours(0, 0, 0, 0);
                const isOverdue = paymentDate < today && !payment.isPaid;
                const isDueToday = paymentDate.getTime() === today.getTime() && !payment.isPaid;
                
                return (
                  <tr 
                    key={payment.id}
                    style={{
                      backgroundColor: isOverdue ? 'rgba(239, 68, 68, 0.1)' : isDueToday ? 'rgba(251, 191, 36, 0.15)' : 'transparent',
                      borderLeft: isOverdue ? '4px solid #ef4444' : isDueToday ? '4px solid #fbbf24' : '4px solid transparent'
                    }}
                  >
                    <td data-label="Schuldenkonto">
                      {isOverdue && <span style={{ color: '#ef4444', fontWeight: 'bold', marginRight: '8px' }}>⚠️</span>}
                      {isDueToday && <span style={{ color: '#f59e0b', fontWeight: 'bold', marginRight: '8px' }}>🔔</span>}
                      {payment.debtAccount?.name}
                    </td>
                    <td data-label="Betrag">{parseFloat(payment.amount.toString()).toFixed(2)} €</td>
                    <td data-label="Fälligkeitsdatum">
                      <span style={{ color: isOverdue ? '#ef4444' : isDueToday ? '#f59e0b' : 'inherit', fontWeight: isOverdue || isDueToday ? 'bold' : 'normal' }}>
                        {new Date(payment.paymentDate).toLocaleDateString('de-DE')}
                        {isOverdue && ' (überfällig!)'}
                        {isDueToday && ' (heute fällig)'}
                      </span>
                    </td>
                    <td data-label="Status">
                      <span className={`badge ${payment.isPaid ? 'badge-success' : isOverdue ? 'badge-danger' : 'badge-warning'}`}>
                        {payment.isPaid ? 'Bezahlt' : isOverdue ? 'Überfällig' : 'Ausstehend'}
                      </span>
                    </td>
                    <td data-label="Aktionen">
                      {!payment.isPaid && (
                        <button
                          onClick={() => {
                            setConfirmModal({ show: true, payment, subscription: null, type: 'debt' });
                            setPaymentAmount(payment.amount.toString());
                          }}
                          className={`btn btn-sm ${isOverdue || isDueToday ? 'btn-danger' : 'btn-success'}`}
                          style={{ padding: '5px 10px', fontSize: '14px' }}
                        >
                          {isOverdue || isDueToday ? 'Jetzt bezahlen!' : 'Zahlung bestätigen'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Fällige wiederkehrende Zahlungen (Abos, Miete, Nebenkosten etc.) */}
      {dueSubscriptions.length > 0 && (
        <div className="card" style={{ 
          marginTop: '20px',
          borderRadius: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: '1px solid #e9ecef',
          background: 'linear-gradient(to right, #fff 0%, #f8f9fa 100%)'
        }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
            <span style={{ 
              background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
              color: 'white',
              padding: '5px 12px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: 'bold'
            }}>
              {dueSubscriptions.length}
            </span>
            <span style={{ fontSize: '24px' }}>🔔</span> Fällige Fixkosten & Abos
          </h2>
          <table style={{ marginTop: '10px' }}>
            <thead>
              <tr>
                <th>Beschreibung</th>
                <th>Kategorie</th>
                <th>Standardkonto</th>
                <th>Betrag</th>
                <th>Buchungstag</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {dueSubscriptions.map((sub) => {
                // Kategorie-Labels
                const categoryLabels: Record<string, string> = {
                  'RENT': 'Miete',
                  'UTILITIES': 'Nebenkosten',
                  'INSURANCE': 'Versicherung',
                  'SUBSCRIPTION': 'Abo',
                  'ENTERTAINMENT': 'Entertainment',
                  'FOOD': 'Lebensmittel',
                  'TRANSPORTATION': 'Transport',
                  'HEALTH': 'Gesundheit',
                  'OTHER': 'Sonstiges'
                };
                
                return (
                  <tr key={sub.id}>
                    <td data-label="Beschreibung"><strong>{sub.description}</strong></td>
                    <td data-label="Kategorie">
                      <span className="badge" style={{ 
                        background: '#f0f0f0', 
                        color: '#333',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {categoryLabels[sub.category] || sub.category}
                      </span>
                    </td>
                    <td data-label="Standardkonto">{sub.account?.name}</td>
                    <td data-label="Betrag" style={{ color: '#dc3545', fontWeight: 'bold' }}>
                      {Math.abs(parseFloat(sub.amount.toString())).toFixed(2)} €
                    </td>
                    <td data-label="Buchungstag">Jeden {sub.bookingDay || new Date(sub.date).getDate()}. des Monats</td>
                    <td data-label="Aktionen">
                      <button
                        onClick={() => {
                          setConfirmModal({ show: true, payment: null, subscription: sub, type: 'subscription' });
                          setPaymentAmount(Math.abs(parseFloat(sub.amount.toString())).toString());
                        }}
                        className="btn btn-sm btn-success"
                        style={{ padding: '5px 10px', fontSize: '14px' }}
                      >
                        Zahlung bestätigen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}



      {/* Zahlungsbestätigung Modal */}
      {confirmModal.show && (
        <div className="modal-overlay" onClick={() => setConfirmModal({ show: false, payment: null, subscription: null, type: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Zahlung bestätigen</h2>
            {confirmModal.type === 'debt' && confirmModal.payment && (
              <p style={{ marginTop: '15px', marginBottom: '20px' }}>
                <strong>Schuldenkonto:</strong> {confirmModal.payment.debtAccount?.name}<br />
                <strong>Fälligkeitsdatum:</strong> {new Date(confirmModal.payment.paymentDate).toLocaleDateString('de-DE')}
              </p>
            )}
            {confirmModal.type === 'subscription' && confirmModal.subscription && (
              <p style={{ marginTop: '15px', marginBottom: '20px' }}>
                <strong>Abo:</strong> {confirmModal.subscription.description}<br />
                <strong>Buchungstag:</strong> Jeden {confirmModal.subscription.bookingDay || new Date(confirmModal.subscription.date).getDate()}. des Monats
              </p>
            )}

            <div className="form-group">
              <label>Betrag (€)</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className="form-control"
                placeholder="Betrag eingeben"
                required
              />
            </div>

            <div className="form-group">
              <label>Von welchem Konto wurde gezahlt?</label>
              <select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="form-control"
                required
              >
                <option value="">-- Konto auswählen --</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} (Saldo: {parseFloat(account.balance.toString()).toFixed(2)} €)
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={handleConfirmPayment}
                className="btn btn-success"
                disabled={!selectedAccountId}
              >
                Bestätigen
              </button>
              <button
                onClick={() => {
                  setConfirmModal({ show: false, payment: null, subscription: null, type: null });
                  setSelectedAccountId('');
                  setPaymentAmount('');
                }}
                className="btn btn-secondary"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: toast.type === 'success' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          padding: '16px 24px',
          borderRadius: '12px',
          boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
          zIndex: 10000,
          animation: 'slideIn 0.3s ease-out',
          fontSize: '15px',
          fontWeight: '500',
          maxWidth: '400px'
        }}>
          {toast.message}
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
